import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import {
  setLeftWord,
  setRightWord,
  setLeftPhrases,
  setRightPhrases,
  setLeftRandom,
  setRightRandom,
} from "../../store/reducers/phraseBuilderPlantel/phraseBuilderPlantel.slice";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect, useCallback } from "react";
import {
  getTalentosSelectoresPlantelService,
  getFrasesDisponiblesPlantelService,
} from "../../services/planteles/talentosPlantel.service";
import { debounce } from "lodash";

const PhraseSelectorsPlantel = ({ plantelId }) => {
  const dispatch = useDispatch();
  const { leftWord, rightWord, leftRandom, rightRandom } = useSelector(
    (s) => s.phraseBuilderPlantel,
  );

  const [leftOptions, setLeftOptions] = useState([]);
  const [rightOptions, setRightOptions] = useState([]);
  const [leftInputValue, setLeftInputValue] = useState("");
  const [rightInputValue, setRightInputValue] = useState("");
  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);

  // Función para cargar talentos desde la API
  const loadTalentos = async (search = "") => {
    try {
      const data = await getTalentosSelectoresPlantelService(plantelId, search);
      return data.map((item) => ({
        value: item.id,
        label: item.label,
      }));
    } catch (error) {
      console.error("Error al cargar talentos:", error);
      return [];
    }
  };

  // Función para cargar frases de un talento
  const loadFrases = async (talentoId, side, random = false) => {
    try {
      const data = await getFrasesDisponiblesPlantelService(plantelId, talentoId, random);
      const mapped = data.map((item) => ({ ...item, label: item.frase }));
      if (side === "left") {
        dispatch(setLeftPhrases(mapped));
      } else {
        dispatch(setRightPhrases(mapped));
      }
    } catch (error) {
      console.error("Error al cargar frases:", error);
    }
  };

  // Debounced search para el selector izquierdo
  const debouncedLoadLeft = useCallback(
    debounce(async (search) => {
      setLeftLoading(true);
      const options = await loadTalentos(search);
      setLeftOptions(options);
      setLeftLoading(false);
    }, 500),
    [plantelId],
  );

  // Debounced search para el selector derecho
  const debouncedLoadRight = useCallback(
    debounce(async (search) => {
      setRightLoading(true);
      const options = await loadTalentos(search);
      setRightOptions(options);
      setRightLoading(false);
    }, 500),
    [plantelId],
  );

  // Cargar talentos al inicio o cuando cambia el plantel
  useEffect(() => {
    if (plantelId) {
      loadTalentos().then((options) => {
        setLeftOptions(options);
        setRightOptions(options);
      });
    }
  }, [plantelId]);

  // Manejar cambio de búsqueda izquierda
  useEffect(() => {
    if (leftInputValue) {
      debouncedLoadLeft(leftInputValue);
    }
  }, [leftInputValue, debouncedLoadLeft]);

  // Manejar cambio de búsqueda derecha
  useEffect(() => {
    if (rightInputValue) {
      debouncedLoadRight(rightInputValue);
    }
  }, [rightInputValue, debouncedLoadRight]);

  // Manejar selección de talento izquierdo
  const handleLeftChange = (option) => {
    dispatch(setLeftWord(option));
    if (option) {
      // Si se selecciona un talento, desactivar random
      if (leftRandom) {
        dispatch(setLeftRandom(false));
      }
      loadFrases(option.value, "left", false);
    } else {
      // Si se limpia el selector y random está activo, mantener frases random
      if (leftRandom) {
        loadFrases(null, "left", true);
      }
    }
  };

  // Manejar selección de talento derecho
  const handleRightChange = (option) => {
    dispatch(setRightWord(option));
    if (option) {
      // Si se selecciona un talento, desactivar random
      if (rightRandom) {
        dispatch(setRightRandom(false));
      }
      loadFrases(option.value, "right", false);
    } else {
      // Si se limpia el selector y random está activo, mantener frases random
      if (rightRandom) {
        loadFrases(null, "right", true);
      }
    }
  };

  // Manejar toggle de random izquierdo
  const handleLeftRandomToggle = () => {
    const newRandom = !leftRandom;
    dispatch(setLeftRandom(newRandom));

    if (newRandom) {
      // Si se activa random, limpiamos el selector y cargamos frases sin talento
      dispatch(setLeftWord(null));
      loadFrases(null, "left", true);
    } else {
      // Si se desactiva random y hay un talento seleccionado, cargamos sus frases
      if (leftWord) {
        loadFrases(leftWord.value, "left", false);
      }
    }
  };

  // Manejar toggle de random derecho
  const handleRightRandomToggle = () => {
    const newRandom = !rightRandom;
    dispatch(setRightRandom(newRandom));

    if (newRandom) {
      // Si se activa random, limpiamos el selector y cargamos frases sin talento
      dispatch(setRightWord(null));
      loadFrases(null, "right", true);
    } else {
      // Si se desactiva random y hay un talento seleccionado, cargamos sus frases
      if (rightWord) {
        loadFrases(rightWord.value, "right", false);
      }
    }
  };

  return (
    <div className="row g-4 mx-0 mb-4">
      {/* Izquierda */}
      <div className="col-12 col-md-6">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <label className="fw-semibold mb-0 d-flex align-items-center gap-2">
                <Icon icon="mdi:arrow-left-bold-outline" width={18} />
                Talento izquierda
              </label>
            </div>

            <div className="row g-2 align-items-stretch">
              <div className="col-12 col-sm">
                <Select
                  className="w-100"
                  options={leftOptions}
                  placeholder="Selecciona un talento"
                  value={leftWord}
                  onChange={handleLeftChange}
                  onInputChange={setLeftInputValue}
                  isLoading={leftLoading}
                  isClearable
                />
              </div>

              <button
                className={`btn ${leftRandom ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={handleLeftRandomToggle}
                title="Frases aleatorias"
              >
                <Icon icon="mdi:shuffle-variant" width={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Derecha */}
      <div className="col-12 col-md-6">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <label className="fw-semibold mb-0 d-flex align-items-center gap-2">
                <Icon icon="mdi:arrow-right-bold-outline" width={18} />
                Talento derecha
              </label>
            </div>

            <div className="row g-2 align-items-stretch">
              <div className="col-12 col-sm">
                <Select
                  className="w-100"
                  options={rightOptions}
                  placeholder="Selecciona un talento"
                  value={rightWord}
                  onChange={handleRightChange}
                  onInputChange={setRightInputValue}
                  isLoading={rightLoading}
                  isClearable
                />
              </div>

              <button
                className={`btn ${rightRandom ? "btn-primary" : "btn-outline-secondary"}`}
                onClick={handleRightRandomToggle}
                title="Frases aleatorias"
              >
                <Icon icon="mdi:shuffle-variant" width={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhraseSelectorsPlantel;
